import React, { useState } from "react";
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  ListSubheader,
  Tooltip,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import ThemeColors from "config/colors";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useNavigate } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
  nested: {
    paddingLeft: theme.spacing(6),
  },
  childLvlTwo: {
    paddingLeft: theme.spacing(8),
  },
  iconMargin: {
    marginRight: "-18px",
    "&.Mui-selected": {
      color: ThemeColors.brandingRed,
    },
  },
}));

const DrawerEntry = ({ item, nested = false, childLvlTwo = false, open }) => {
  const classes = useStyles();
  const [collapseOpen, setCollapseOpen] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleClick = (item) => {
    if (item?.type === "item" && !isTabSelected(item)) {
      navigate(item.url);
    } else if (item?.type === "collapse") {
      setCollapseOpen(!collapseOpen);
    }
  };

  const isTabSelected = (item) => {
    return window?.location?.pathname === item?.url;
  };

  const userPermissions = useSelector(
    (state) => state?.auth?.user?.permissions ?? []
  );

  // Check if the user has permission to see this item.
  const showDrawerItem = () => {
    if (item?.permissions && item?.permissions?.length > 0) {
      let result = false;
      for (const perm of item.permissions) {
        for (const userPerm of userPermissions) {
          result = result || perm === userPerm;
        }
      }
      return result;
    }
    return true;
  };

  if (!showDrawerItem()) {
    return null;
  }

  // Define the list item content
  const listItemContent = (
    <ListItem
      button
      sx={{ textAlign: "inherit" }}
      selected={isTabSelected(item)}
      onClick={() => handleClick(item)}
      className={`${nested ? classes.nested : ""} ${
        childLvlTwo ? classes.childLvlTwo : ""
      }`}
    >
      {item?.icon &&
        (typeof item?.icon === "string" ? (
          <ListItemIcon className={classes.iconMargin}>
            <Box
              sx={{
                backgroundImage: `url(${item?.icon})`,
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
                width: 30,
                height: 30,
                marginLeft: "-4px",
              }}
            />
          </ListItemIcon>
        ) : (
          <ListItemIcon
            className={classes.iconMargin}
            sx={{ color: ThemeColors.greyColor }}
          >
            {item?.icon}
          </ListItemIcon>
        ))}
      <ListItemText primary={t(item.title)} />
      {item?.type === "collapse" &&
        (collapseOpen ? <ExpandLess /> : <ExpandMore />)}
    </ListItem>
  );

  return (
    <>
      {item?.type !== "title" &&
        (open ? (
          listItemContent
        ) : (
          <Tooltip title={t(item.title)} placement='right'>
            {listItemContent}
          </Tooltip>
        ))}

      {item?.type === "title" && (
        <List
          component='div'
          subheader={
            item?.title && (
              <ListSubheader
                component='div'
                id='nested-list-subheader'
                style={{
                  color: ThemeColors.blackColor,
                  fontWeight: 700,
                  position: "inherit",
                }}
              >
                {t(item.title)}
              </ListSubheader>
            )
          }
          disablePadding
        >
          {item?.children?.map((child, index) => (
            <DrawerEntry key={index} item={child} open={open} />
          ))}
        </List>
      )}

      {item?.type === "collapse" && (
        <Collapse in={collapseOpen} timeout='auto' unmountOnExit>
          <List
            component='div'
            subheader={
              item?.subheader && (
                <ListSubheader component='div' id='nested-list-subheader'>
                  {item.subheader}
                </ListSubheader>
              )
            }
            disablePadding
          >
            {item?.children?.map((child, index) => (
              <DrawerEntry
                key={index}
                item={child}
                nested
                childLvlTwo={child.childLvlTwo}
                open={open}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

export default DrawerEntry;
